import React, { useState, useEffect, useCallback } from "react";
import noimg from "../../../../assets/images/noimg.webp";
import {
  industry,
  concentration,
  concentrationProduct,
  educationDegree,
  operationRange,
} from "../../../../mock/DropdownOptions";
import { years } from "../../../../functions/YearGenerate";
import { getUserWiseProfile } from "../../../../service/DashboardService";
import { uploadToS3 } from "../../../../functions/uploadToS3";
import { toast } from "react-toastify";

const SelectInput = ({ name, value, options, onChange, profile }) => {
  return (
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
    >
      {profile === "Product"
        ? concentrationProduct.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))
        : options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
    </select>
  );
};

export const MyProfile = ({ userDetail: initialUserDetail }) => {
  const [userDetail, setUserDetail] = useState(initialUserDetail);
  const occupation = userDetail.Occupation;
  const [editable, setEditable] = useState(false);
  const [updatedUserDetail, setUpdatedUserDetail] = useState({ ...initialUserDetail });
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);

  // Sync when parent passes new data
  useEffect(() => {
    setUserDetail(initialUserDetail);
    if (!editable) {
      setUpdatedUserDetail({ ...initialUserDetail });
    }
  }, [initialUserDetail, editable]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUserDetail((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEdit = () => {
    setEditable(true);
  };

  const refreshProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const userId = localStorage.getItem("userId");
      const freshData = await getUserWiseProfile(userId, token);
      setUserDetail(freshData);
      setUpdatedUserDetail({ ...freshData });
    } catch (err) {
      console.error("Failed to refresh profile:", err);
    }
  }, []);

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPic(true);
    try {
      const url = await uploadToS3(file, "profile");
      setUpdatedUserDetail((prev) => ({ ...prev, ProfilePicture: url }));
      toast.success("Profile picture uploaded!");
    } catch (err) {
      console.error("Profile picture upload failed:", err);
      toast.error("Failed to upload profile picture.");
    } finally {
      setUploadingPic(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("accessToken");
      const userId = parseInt(localStorage.getItem("userId"), 10);

      // Build a clean payload that strictly matches SaveUseReq @JsonProperty keys and types.
      // Sending extra/unrecognized fields (e.g. nested entity lists, ProjectList, CollaborationProjectList)
      // or wrong types (String instead of Long for ContactPhoneNumber, String instead of Country object)
      // causes Spring's HttpMessageConverter to fail with 500 before even reaching the service.
      const payload = {
        UserId: userId,
        Email: updatedUserDetail.Email || null,
        Name: updatedUserDetail.Name || null,
        ShortBio: updatedUserDetail.ShortBio || null,
        ProfessionPositionName: updatedUserDetail.ProfessionPositionName || null,
        // Education blocks
        EducationDegree1: updatedUserDetail.EducationDegree1 || null,
        EducationDegree2: updatedUserDetail.EducationDegree2 || null,
        EducationDegree3: updatedUserDetail.EducationDegree3 || null,
        EducationDegree4: updatedUserDetail.EducationDegree4 || null,
        EducationDegree5: updatedUserDetail.EducationDegree5 || null,
        EducationDegree6: updatedUserDetail.EducationDegree6 || null,
        EducationDegree7: updatedUserDetail.EducationDegree7 || null,
        EducationDegree8: updatedUserDetail.EducationDegree8 || null,
        EducationDegree9: updatedUserDetail.EducationDegree9 || null,
        EducationDegree10: updatedUserDetail.EducationDegree10 || null,
        EducationalInstitute1: updatedUserDetail.EducationalInstitute1 || null,
        EducationalInstitute2: updatedUserDetail.EducationalInstitute2 || null,
        EducationalInstitute3: updatedUserDetail.EducationalInstitute3 || null,
        EducationalInstitute4: updatedUserDetail.EducationalInstitute4 || null,
        EducationalInstitute5: updatedUserDetail.EducationalInstitute5 || null,
        EducationalInstitute6: updatedUserDetail.EducationalInstitute6 || null,
        EducationalInstitute7: updatedUserDetail.EducationalInstitute7 || null,
        EducationalInstitute8: updatedUserDetail.EducationalInstitute8 || null,
        EducationalInstitute9: updatedUserDetail.EducationalInstitute9 || null,
        EducationalInstitute10: updatedUserDetail.EducationalInstitute10 || null,
        EducationTitle1: updatedUserDetail.EducationTitle1 || null,
        EducationTitle2: updatedUserDetail.EducationTitle2 || null,
        EducationTitle3: updatedUserDetail.EducationTitle3 || null,
        EducationTitle4: updatedUserDetail.EducationTitle4 || null,
        EducationTitle5: updatedUserDetail.EducationTitle5 || null,
        EducationTitle6: updatedUserDetail.EducationTitle6 || null,
        EducationTitle7: updatedUserDetail.EducationTitle7 || null,
        EducationTitle8: updatedUserDetail.EducationTitle8 || null,
        EducationTitle9: updatedUserDetail.EducationTitle9 || null,
        EducationTitle10: updatedUserDetail.EducationTitle10 || null,
        // EducationYear fields must be Integer or null
        EducationYear1: updatedUserDetail.EducationYear1 ? parseInt(updatedUserDetail.EducationYear1, 10) : null,
        EducationYear2: updatedUserDetail.EducationYear2 ? parseInt(updatedUserDetail.EducationYear2, 10) : null,
        EducationYear3: updatedUserDetail.EducationYear3 ? parseInt(updatedUserDetail.EducationYear3, 10) : null,
        EducationYear4: updatedUserDetail.EducationYear4 ? parseInt(updatedUserDetail.EducationYear4, 10) : null,
        EducationYear5: updatedUserDetail.EducationYear5 ? parseInt(updatedUserDetail.EducationYear5, 10) : null,
        EducationYear6: updatedUserDetail.EducationYear6 ? parseInt(updatedUserDetail.EducationYear6, 10) : null,
        EducationYear7: updatedUserDetail.EducationYear7 ? parseInt(updatedUserDetail.EducationYear7, 10) : null,
        EducationYear8: updatedUserDetail.EducationYear8 ? parseInt(updatedUserDetail.EducationYear8, 10) : null,
        EducationYear9: updatedUserDetail.EducationYear9 ? parseInt(updatedUserDetail.EducationYear9, 10) : null,
        EducationYear10: updatedUserDetail.EducationYear10 ? parseInt(updatedUserDetail.EducationYear10, 10) : null,
        // Professional experience
        CompanyProfessionalExperience1: updatedUserDetail.CompanyProfessionalExperience1 || null,
        CompanyProfessionalExperience2: updatedUserDetail.CompanyProfessionalExperience2 || null,
        CompanyProfessionalExperience3: updatedUserDetail.CompanyProfessionalExperience3 || null,
        CompanyProfessionalExperience4: updatedUserDetail.CompanyProfessionalExperience4 || null,
        CompanyProfessionalExperience5: updatedUserDetail.CompanyProfessionalExperience5 || null,
        CompanyProfessionalExperience6: updatedUserDetail.CompanyProfessionalExperience6 || null,
        CompanyProfessionalExperience7: updatedUserDetail.CompanyProfessionalExperience7 || null,
        CompanyProfessionalExperience8: updatedUserDetail.CompanyProfessionalExperience8 || null,
        CompanyProfessionalExperience9: updatedUserDetail.CompanyProfessionalExperience9 || null,
        CompanyProfessionalExperience10: updatedUserDetail.CompanyProfessionalExperience10 || null,
        PositionProfessionalExperience1: updatedUserDetail.PositionProfessionalExperience1 || null,
        PositionProfessionalExperience2: updatedUserDetail.PositionProfessionalExperience2 || null,
        PositionProfessionalExperience3: updatedUserDetail.PositionProfessionalExperience3 || null,
        PositionProfessionalExperience4: updatedUserDetail.PositionProfessionalExperience4 || null,
        PositionProfessionalExperience5: updatedUserDetail.PositionProfessionalExperience5 || null,
        PositionProfessionalExperience6: updatedUserDetail.PositionProfessionalExperience6 || null,
        PositionProfessionalExperience7: updatedUserDetail.PositionProfessionalExperience7 || null,
        PositionProfessionalExperience8: updatedUserDetail.PositionProfessionalExperience8 || null,
        PositionProfessionalExperience9: updatedUserDetail.PositionProfessionalExperience9 || null,
        PositionProfessionalExperience10: updatedUserDetail.PositionProfessionalExperience10 || null,
        // YearProfessionalExperience must be Integer or null
        YearProfessionalExperience1: updatedUserDetail.YearProfessionalExperience1 ? parseInt(updatedUserDetail.YearProfessionalExperience1, 10) : null,
        YearProfessionalExperience2: updatedUserDetail.YearProfessionalExperience2 ? parseInt(updatedUserDetail.YearProfessionalExperience2, 10) : null,
        YearProfessionalExperience3: updatedUserDetail.YearProfessionalExperience3 ? parseInt(updatedUserDetail.YearProfessionalExperience3, 10) : null,
        YearProfessionalExperience4: updatedUserDetail.YearProfessionalExperience4 ? parseInt(updatedUserDetail.YearProfessionalExperience4, 10) : null,
        YearProfessionalExperience5: updatedUserDetail.YearProfessionalExperience5 ? parseInt(updatedUserDetail.YearProfessionalExperience5, 10) : null,
        YearProfessionalExperience6: updatedUserDetail.YearProfessionalExperience6 ? parseInt(updatedUserDetail.YearProfessionalExperience6, 10) : null,
        YearProfessionalExperience7: updatedUserDetail.YearProfessionalExperience7 ? parseInt(updatedUserDetail.YearProfessionalExperience7, 10) : null,
        YearProfessionalExperience8: updatedUserDetail.YearProfessionalExperience8 ? parseInt(updatedUserDetail.YearProfessionalExperience8, 10) : null,
        YearProfessionalExperience9: updatedUserDetail.YearProfessionalExperience9 ? parseInt(updatedUserDetail.YearProfessionalExperience9, 10) : null,
        YearProfessionalExperience10: updatedUserDetail.YearProfessionalExperience10 ? parseInt(updatedUserDetail.YearProfessionalExperience10, 10) : null,
        // Contact – ContactPhoneNumber must be Long (number) or null, NOT a string
        ContactPhoneNumber: updatedUserDetail.ContactPhoneNumber ? parseInt(updatedUserDetail.ContactPhoneNumber, 10) : null,
        ContactEmailAddress: updatedUserDetail.ContactEmailAddress || null,
        WebsiteLink: updatedUserDetail.WebsiteLink || null,
        // Country must be the full Country object {countryId, countryName} as returned by the API, or null
        Country: updatedUserDetail.Country && updatedUserDetail.Country.countryId
          ? { countryId: updatedUserDetail.Country.countryId, countryName: updatedUserDetail.Country.countryName }
          : null,
        // Social links
        FacebookIconLink: updatedUserDetail.FacebookIconLink || null,
        InstagramIconLink: updatedUserDetail.InstagramIconLink || null,
        IssueIconLink: updatedUserDetail.IssueIconLink || null,
        XIconLink: updatedUserDetail.XIconLink || null,
        YoutubeIconLink: updatedUserDetail.YoutubeIconLink || null,
        // Other
        ProfilePicture: updatedUserDetail.ProfilePicture || null,
        OperationRange: updatedUserDetail.OperationRange || null,
        MagazineShippingAddress: updatedUserDetail.MagazineShippingAddress || null,
        IconsOfProfessionalSoftwares: updatedUserDetail.IconsOfProfessionalSoftwares || null,
        StartYear: updatedUserDetail.StartYear ? parseInt(updatedUserDetail.StartYear, 10) : null,
        Occupation: updatedUserDetail.Occupation || null,
        Concentration: updatedUserDetail.Concentration || null,
        Industry: updatedUserDetail.Industry || null,
        SoftwareLogo: updatedUserDetail.SoftwareLogo || [],
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/signup/updateUsers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Server error response:", errorBody);
        throw new Error(`Server responded with ${response.status}`);
      }

      toast.success("Profile updated successfully!");
      setEditable(false);
      await refreshProfile();
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-10">
        <h3 className="text-md font-bold text-blue-400 dark:text-white mb-2">
          My Profile
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 gap-x-4">
            <div className="relative">
              <img
                className="h-20 w-20 flex-none rounded-full bg-gray-50 object-cover"
                src={(editable ? updatedUserDetail.ProfilePicture : userDetail.ProfilePicture) || noimg}
                alt="Profile"
              />
              {editable && (
                <label className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full p-1 cursor-pointer hover:bg-blue-600 text-xs">
                  {uploadingPic ? "..." : "✎"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="hidden"
                    disabled={uploadingPic}
                  />
                </label>
              )}
            </div>
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold leading-6 text-gray-800">
                {userDetail.Name || "NA"}
              </p>
              <p className="mt-1 truncate text-sm leading-5 text-gray-400">
                {userDetail.Occupation || "NA"}
              </p>

              <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                {userDetail.Country?.countryName || "NA"}
              </p>
            </div>
          </div>
          <button
            type="submit"
            onClick={editable ? handleSubmit : handleEdit}
            disabled={saving}
            className="py-2.5 px-5 me-2 mb-2 flex items-center gap-2 text-md font-medium text-blue-300 focus:outline-none  rounded-full border border-blue-300   focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : editable ? "Save" : "Edit"}
          </button>
        </div>
      </div>

      <div>
        <h1 className="text-md font-semibold text-gray-800">PERSONAL INFO</h1>
        <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12">
          <div className="sm:col-span-3">
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold leading-6 text-blue-400">
                Email
              </p>
              <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                {userDetail.Email || "NA"}
              </p>
            </div>
          </div>

          <div className="sm:col-span-6">
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold leading-6 text-blue-400">
                Biography
              </p>
              {editable ? (
                <input
                  type="text"
                  name="ShortBio"
                  value={updatedUserDetail.ShortBio}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                />
              ) : (
                <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                  {userDetail.ShortBio || "NA"}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <h1 className="text-md font-semibold text-gray-800">
            PROFESSIONAL INFO
          </h1>

          <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
            {occupation !== "Product" && (
              <div className="sm:col-span-3">
                <div className="min-w-0 flex-auto">
                  <p className="text-sm font-semibold leading-6 text-blue-400">
                    Professional Position
                  </p>
                  {editable ? (
                    <input
                      type="text"
                      name="ProfessionPositionName"
                      value={updatedUserDetail.ProfessionPositionName}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                    />
                  ) : (
                    <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                      {userDetail?.ProfessionPositionName || "NA"}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Start Year in the Industry
                </p>
                {editable ? (
                  <SelectInput
                    name="StartYear"
                    value={updatedUserDetail.StartYear}
                    options={years}
                    onChange={handleChange}
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail.StartYear || "NA"}
                  </p>
                )}
              </div>
            </div>
            {occupation === "Product" && (
              <div className="sm:col-span-3">
                <div className="min-w-0 flex-auto">
                  <p className="text-sm font-semibold leading-6 text-blue-400">
                    Operations
                  </p>
                  {editable ? (
                    <SelectInput
                      name="OperationRange"
                      value={updatedUserDetail.OperationRange}
                      options={operationRange}
                      onChange={handleChange}
                    />
                  ) : (
                    <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                      {userDetail?.OperationRange || "NA"}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Industry
                </p>
                {editable ? (
                  <SelectInput
                    name="Industry"
                    value={updatedUserDetail.Industry}
                    options={industry}
                    onChange={handleChange}
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.Industry || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Concentration
                </p>
                {editable ? (
                  <SelectInput
                    name="Concentration"
                    value={updatedUserDetail.Concentration}
                    options={concentration}
                    onChange={handleChange}
                    profile={occupation}
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.Concentration || "NA"}
                  </p>
                )}
              </div>
            </div>
          </div>

          {occupation !== "Product" && (
            <>
              <span className="text-gray-400 text-sm  font-medium mt-5 flex items-center gap-2 cursor-pointer">
                Professional Experience 1
              </span>
              <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Company Name
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="CompanyProfessionalExperience1"
                        value={updatedUserDetail.CompanyProfessionalExperience1}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.CompanyProfessionalExperience1 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Position
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="PositionProfessionalExperience1"
                        value={
                          updatedUserDetail.PositionProfessionalExperience1
                        }
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.PositionProfessionalExperience1 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Year
                    </p>
                    {editable ? (
                      <SelectInput
                        name="YearProfessionalExperience1"
                        value={updatedUserDetail.YearProfessionalExperience1}
                        options={years}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.YearProfessionalExperience1 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <span className="text-gray-400 text-sm  font-medium mt-5 flex items-center gap-2 cursor-pointer">
                Professional Experience 2
              </span>
              <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Company Name
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="CompanyProfessionalExperience2"
                        value={updatedUserDetail.CompanyProfessionalExperience2}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.CompanyProfessionalExperience2 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Position
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="PositionProfessionalExperience2"
                        value={
                          updatedUserDetail.PositionProfessionalExperience2
                        }
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.PositionProfessionalExperience2 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Year
                    </p>
                    {editable ? (
                      <SelectInput
                        name="YearProfessionalExperience2"
                        value={updatedUserDetail.YearProfessionalExperience2}
                        options={years}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.YearProfessionalExperience2 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <span className="text-gray-400 text-sm  font-medium mt-5 flex items-center gap-2 cursor-pointer">
                Professional Experience 3
              </span>
              <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Company Name
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="CompanyProfessionalExperience3"
                        value={updatedUserDetail.CompanyProfessionalExperience3}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.CompanyProfessionalExperience3 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Position
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="PositionProfessionalExperience3"
                        value={
                          updatedUserDetail.PositionProfessionalExperience3
                        }
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.PositionProfessionalExperience3 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Year
                    </p>
                    {editable ? (
                      <SelectInput
                        name="YearProfessionalExperience3"
                        value={updatedUserDetail.YearProfessionalExperience3}
                        options={years}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.YearProfessionalExperience3 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Experience 4-10 (dynamic) */}
              {[4, 5, 6, 7, 8, 9, 10].map((n) => {
                const compKey = `CompanyProfessionalExperience${n}`;
                const posKey = `PositionProfessionalExperience${n}`;
                const yearKey = `YearProfessionalExperience${n}`;
                const hasData = userDetail?.[compKey] || userDetail?.[posKey] || userDetail?.[yearKey];
                if (!hasData && !editable) return null;
                return (
                  <React.Fragment key={`exp-${n}`}>
                    <span className="text-gray-400 text-sm font-medium mt-5 flex items-center gap-2 cursor-pointer">
                      Professional Experience {n}
                    </span>
                    <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                      <div className="sm:col-span-4">
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm font-semibold leading-6 text-blue-400">Company Name</p>
                          {editable ? (
                            <input type="text" name={compKey} value={updatedUserDetail[compKey] || ""} onChange={handleChange}
                              className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500" />
                          ) : (
                            <p className="mt-1 truncate text-sm leading-5 text-gray-500">{userDetail?.[compKey] || "NA"}</p>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-4">
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm font-semibold leading-6 text-blue-400">Position</p>
                          {editable ? (
                            <input type="text" name={posKey} value={updatedUserDetail[posKey] || ""} onChange={handleChange}
                              className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500" />
                          ) : (
                            <p className="mt-1 truncate text-sm leading-5 text-gray-500">{userDetail?.[posKey] || "NA"}</p>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-4">
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm font-semibold leading-6 text-blue-400">Year</p>
                          {editable ? (
                            <SelectInput name={yearKey} value={updatedUserDetail[yearKey]} options={years} onChange={handleChange} />
                          ) : (
                            <p className="mt-1 truncate text-sm leading-5 text-gray-500">{userDetail?.[yearKey] || "NA"}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>

        {occupation !== "Product" && (
          <>
            <div className="mt-5">
              <h1 className="text-md font-semibold text-gray-800">
                EDUCATIONAL INFO
              </h1>
              <span className="text-gray-400 text-sm font-medium mt-5 flex items-center gap-2 cursor-pointer">
                Education 1
              </span>
              <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Education Degree
                    </p>
                    {editable ? (
                      <SelectInput
                        name="EducationDegree1"
                        value={updatedUserDetail.EducationDegree1}
                        options={educationDegree}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationDegree1 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Institute or School
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="EducationalInstitute1"
                        value={updatedUserDetail.EducationalInstitute1}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationalInstitute1 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Title or Career
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="EducationTitle1"
                        value={updatedUserDetail.EducationTitle1}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationTitle1 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Finish Year
                    </p>
                    {editable ? (
                      <SelectInput
                        name="EducationYear1"
                        value={updatedUserDetail.EducationYear1}
                        options={years}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationYear1 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <span className="text-gray-400 text-sm  font-medium mt-5 flex items-center gap-2 cursor-pointer">
                Education 2
              </span>
              <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Education Degree
                    </p>
                    {editable ? (
                      <SelectInput
                        name="EducationDegree2"
                        value={updatedUserDetail.EducationDegree2}
                        options={educationDegree}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationDegree2 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Institute or School
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="EducationalInstitute2"
                        value={updatedUserDetail.EducationalInstitute2}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationalInstitute2 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Title or Career
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="EducationTitle2"
                        value={updatedUserDetail.EducationTitle2}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationTitle2 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Finish Year
                    </p>
                    {editable ? (
                      <SelectInput
                        name="EducationYear2"
                        value={updatedUserDetail.EducationYear2}
                        options={years}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationYear2 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <span className="text-gray-400 text-sm  font-medium mt-5 flex items-center gap-2 cursor-pointer">
                Education 3
              </span>
              <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Education Degree
                    </p>
                    {editable ? (
                      <SelectInput
                        name="EducationDegree3"
                        value={updatedUserDetail.EducationDegree3}
                        options={educationDegree}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationDegree3 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Institute or School
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="EducationalInstitute3"
                        value={updatedUserDetail.EducationalInstitute3}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationalInstitute3 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Title or Career
                    </p>
                    {editable ? (
                      <input
                        type="text"
                        name="EducationTitle3"
                        value={updatedUserDetail.EducationTitle3}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationTitle3 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm font-semibold leading-6 text-blue-400">
                      Finish Year
                    </p>
                    {editable ? (
                      <SelectInput
                        name="EducationYear3"
                        value={updatedUserDetail.EducationYear3}
                        options={years}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                        {userDetail?.EducationYear3 || "NA"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Education 4-10 (dynamic) */}
              {[4, 5, 6, 7, 8, 9, 10].map((n) => {
                const degKey = `EducationDegree${n}`;
                const instKey = `EducationalInstitute${n}`;
                const titleKey = `EducationTitle${n}`;
                const yearKey = `EducationYear${n}`;
                const hasData = userDetail?.[degKey] || userDetail?.[instKey] || userDetail?.[titleKey] || userDetail?.[yearKey];
                if (!hasData && !editable) return null;
                return (
                  <React.Fragment key={`edu-${n}`}>
                    <span className="text-gray-400 text-sm font-medium mt-5 flex items-center gap-2 cursor-pointer">
                      Education {n}
                    </span>
                    <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12 border-b border-gray-300 p-2">
                      <div className="sm:col-span-3">
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm font-semibold leading-6 text-blue-400">Education Degree</p>
                          {editable ? (
                            <SelectInput name={degKey} value={updatedUserDetail[degKey]} options={educationDegree} onChange={handleChange} />
                          ) : (
                            <p className="mt-1 truncate text-sm leading-5 text-gray-500">{userDetail?.[degKey] || "NA"}</p>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-3">
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm font-semibold leading-6 text-blue-400">Institute or School</p>
                          {editable ? (
                            <input type="text" name={instKey} value={updatedUserDetail[instKey] || ""} onChange={handleChange}
                              className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500" />
                          ) : (
                            <p className="mt-1 truncate text-sm leading-5 text-gray-500">{userDetail?.[instKey] || "NA"}</p>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-3">
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm font-semibold leading-6 text-blue-400">Title or Career</p>
                          {editable ? (
                            <input type="text" name={titleKey} value={updatedUserDetail[titleKey] || ""} onChange={handleChange}
                              className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500" />
                          ) : (
                            <p className="mt-1 truncate text-sm leading-5 text-gray-500">{userDetail?.[titleKey] || "NA"}</p>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-3">
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm font-semibold leading-6 text-blue-400">Finish Year</p>
                          {editable ? (
                            <SelectInput name={yearKey} value={updatedUserDetail[yearKey]} options={years} onChange={handleChange} />
                          ) : (
                            <p className="mt-1 truncate text-sm leading-5 text-gray-500">{userDetail?.[yearKey] || "NA"}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="mt-5">
              <h1 className="text-md font-semibold text-gray-800">
                SOFTWARE EXPERIENCE
              </h1>
              {editable ? (
                <div className="mt-5 space-y-2">
                  {(updatedUserDetail.SoftwareLogo || []).map((logo, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <img src={logo} alt={`software-${idx}`} className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                      <input
                        type="text"
                        value={logo}
                        onChange={(e) => {
                          const newLogos = [...(updatedUserDetail.SoftwareLogo || [])];
                          newLogos[idx] = e.target.value;
                          setUpdatedUserDetail((prev) => ({ ...prev, SoftwareLogo: newLogos }));
                        }}
                        className="flex-1 border border-gray-300 rounded-md py-1 px-2 text-sm shadow-sm focus:outline-none focus:border-blue-500"
                        placeholder="Logo URL"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newLogos = (updatedUserDetail.SoftwareLogo || []).filter((_, i) => i !== idx);
                          setUpdatedUserDetail((prev) => ({ ...prev, SoftwareLogo: newLogos }));
                        }}
                        className="text-red-500 hover:text-red-700 text-sm font-bold"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newLogos = [...(updatedUserDetail.SoftwareLogo || []), ""];
                      setUpdatedUserDetail((prev) => ({ ...prev, SoftwareLogo: newLogos }));
                    }}
                    className="text-blue-500 hover:text-blue-700 text-sm font-medium mt-2"
                  >
                    + Add Software Logo
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-10 gap-2 mt-5">
                  {userDetail?.SoftwareLogo?.map((logo, idx) => (
                    <img key={idx} src={logo} alt="software exp" className="w-10" />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <div className="mt-5">
          <h1 className="text-md font-semibold text-gray-800">CONTACT INFO</h1>
          <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-12">
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Contact Email Address
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="ContactEmailAddress"
                    value={updatedUserDetail.ContactEmailAddress}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.ContactEmailAddress || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Contact Phone Number
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="ContactPhoneNumber"
                    value={updatedUserDetail.ContactPhoneNumber}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.ContactPhoneNumber || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Website Link
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="WebsiteLink"
                    value={updatedUserDetail.WebsiteLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.WebsiteLink || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Facebook URL
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="FacebookIconLink"
                    value={updatedUserDetail.FacebookIconLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.FacebookIconLink || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Instagram URL
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="InstagramIconLink"
                    value={updatedUserDetail.InstagramIconLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.InstagramIconLink || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Issuu URL
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="IssueIconLink"
                    value={updatedUserDetail.IssueIconLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.IssueIconLink || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  X URL
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="XIconLink"
                    value={updatedUserDetail.XIconLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.XIconLink || "NA"}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-blue-400">
                  Youtube URL
                </p>
                {editable ? (
                  <input
                    type="text"
                    name="YoutubeIconLink"
                    value={updatedUserDetail.YoutubeIconLink}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
                  />
                ) : (
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                    {userDetail?.YoutubeIconLink || "NA"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-5">
          <h1 className="text-md font-semibold text-gray-800 ">OTHER INFO</h1>
          <div className="min-w-0 flex-auto">
            <p className="text-sm font-semibold leading-6 text-blue-400">
              Magazine Shipping Address
            </p>
            {editable ? (
              <input
                type="text"
                name="MagazineShippingAddress"
                value={updatedUserDetail.MagazineShippingAddress}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
              />
            ) : (
              <p className="mt-1 truncate text-sm leading-5 text-gray-500">
                {userDetail?.MagazineShippingAddress || "NA"}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
